import React from 'react';
import ReactHtmlParser from 'react-html-parser';

const OPTIONS = [
  { key: 'A', field: 'option1' },
  { key: 'B', field: 'option2' },
  { key: 'C', field: 'option3' },
  { key: 'D', field: 'option4' },
];

const cleanValue = value =>
  String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const normalizeAnswer = (answer, question) => {
  if (answer === null || answer === undefined) return '';

  const raw = String(answer).trim();
  const upper = raw.toUpperCase();

  // New backend format: A / B / C / D
  if (['A', 'B', 'C', 'D'].includes(upper)) {
    return upper;
  }

  // Backward compatibility: old records stored the actual option text.
  const cleaned = cleanValue(raw);
  const match = OPTIONS.find(
    option => cleanValue(question?.[option.field]) === cleaned
  );

  return match ? match.key : '';
};

const QuestionCard = ({
  question,
  questionIndex,
  savedbackup,
  markedbackup,
  showAnswer,
}) => {
  if (!question) {
    return (
      <section className="question-card empty-question">
        <div className="empty-question-icon">?</div>
        <div>
          <h2>Select a section</h2>
          <p>Choose a section above to view its questions and answers.</p>
        </div>
      </section>
    );
  }

  const savedItem = Array.isArray(savedbackup)
    ? savedbackup.find(
        item => String(item.q_id) === String(question.q_id)
      )
    : null;

  const markedItem = Array.isArray(markedbackup)
    ? markedbackup.find(
        item => String(item.q_id) === String(question.q_id)
      )
    : null;

  const selectedKey = normalizeAnswer(savedItem?.answer, question);
  const correctKey = normalizeAnswer(question.answer, question);
  const isReviewed = Boolean(markedItem);

  let resultStatus = 'unattempted';
  if (selectedKey && correctKey) {
    resultStatus = selectedKey === correctKey ? 'correct' : 'incorrect';
  } else if (isReviewed) {
    resultStatus = 'review';
  }

  const statusConfig = {
    correct: {
      label: 'Correct Answer',
      className: 'result-correct',
      icon: '✓',
    },
    incorrect: {
      label: 'Incorrect Answer',
      className: 'result-incorrect',
      icon: '×',
    },
    review: {
      label: 'Marked for Review',
      className: 'result-review',
      icon: '★',
    },
    unattempted: {
      label: 'Not Attempted',
      className: 'result-unattempted',
      icon: '○',
    },
  };

  const result = statusConfig[resultStatus];

  return (
    <section className={`question-card result-card ${result.className}`}>
      <div className="question-meta">
        <div className="question-number">
          <span>Question</span>
          <strong>{(questionIndex || 0) + 1}</strong>
        </div>

        {question.year && (
          <span className="question-year">
            {ReactHtmlParser(question.year)}
          </span>
        )}

        <span className={`result-status ${result.className}`}>
          <span className="result-status-icon">{result.icon}</span>
          {result.label}
        </span>
      </div>

      <div className="question-body">
        <div className="question-text">
          {ReactHtmlParser(question.question)}
        </div>

        <div className="answer-legend">
          <span><i className="legend-box correct" /> Correct</span>
          <span><i className="legend-box incorrect" /> Your answer</span>
          <span><i className="legend-box review" /> Review</span>
        </div>

        <div className="answer-options">
          {OPTIONS.map(({ key, field }) => {
            const isSelected = selectedKey === key;
            const isCorrect = correctKey === key;
            const isReview = isReviewed && isSelected;

            let optionStatus = '';
            if (isCorrect && isSelected) {
              optionStatus = 'option-correct-selected';
            } else if (isCorrect) {
              optionStatus = 'option-correct';
            } else if (isSelected && isReview) {
              optionStatus = 'option-review';
            } else if (isSelected) {
              optionStatus = 'option-incorrect';
            }

            return (
              <div
                key={field}
                className={`answer-option solution-option ${optionStatus}`}
              >
                <span className="answer-radio">
                  {isCorrect ? '✓' : isSelected ? '×' : ''}
                </span>

                <span className="answer-letter">{key}</span>

                <span className="answer-content">
                  {ReactHtmlParser(question[field] || '')}
                </span>

                <span className="option-status-label">
                  {isCorrect && isSelected && 'Your answer • Correct'}
                  {isCorrect && !isSelected && 'Correct answer'}
                  {!isCorrect && isSelected && isReview && 'Your answer • Review'}
                  {!isCorrect && isSelected && !isReview && 'Your answer • Incorrect'}
                </span>
              </div>
            );
          })}
        </div>

        {showAnswer && (
          <div className="correct-answer">
            <div className="correct-answer-title">
              Correct Answer
            </div>
            <div className="correct-answer-value">
              <strong>{correctKey || '—'}</strong>
              {correctKey && (
                <span>
                  {ReactHtmlParser(
                    question[`option${correctKey.charCodeAt(0) - 64}`] || ''
                  )}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default QuestionCard;
